import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Microscope, Droplets, ShieldCheck, Activity, FlaskConical, Layers, ArrowUpRight } from "lucide-react";
import { Section, Overline, CTAButton, PageHero } from "@/components/Bits";
import ScrollHero from "@/components/ScrollHero";
import { IMAGES, useContent } from "@/data/site";
import { useI18n } from "@/i18n";
import { api } from "@/lib/api";
import { catalogForLocale, validCatalog } from "@/data/catalog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const ICONS = [Microscope, Layers, Droplets, ShieldCheck, Activity, FlaskConical];

const Counter = ({ to, suffix = "" }) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    let f = 0;
    const id = setInterval(() => {
      f += 1;
      setV(Math.round(to * Math.min(1, f / 40)));
      if (f >= 40) clearInterval(id);
    }, 22);
    return () => clearInterval(id);
  }, [to]);
  return <span className="font-mono-dt">{v}{suffix}</span>;
};

export function Home() {
  const { t, locale } = useI18n();
  const c = useContent(locale);
  return (
    <>
      <ScrollHero />
      <Section className="grid gap-12 border-b border-black/10 py-16 md:grid-cols-12 md:py-20">
        <div className="md:col-span-6">
          <Overline>{t("home.figures")}</Overline>
          <div className="mt-8 grid grid-cols-3 gap-6 border-t border-black/10 pt-8">
            {[[14, t("home.figures.params")], [2, t("home.figures.days")], [0, t("home.figures.labs")]].map(([v, l]) => (
              <div key={l}>
                <p className="text-2xl font-light"><Counter to={v} /></p>
                <p className="mt-1 text-xs text-zinc-500">{l}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 max-w-md text-base leading-relaxed text-zinc-600">{t("home.notdna.body")}</p>
        </div>
        <div className="md:col-span-5 md:col-start-8">
          <div className="border border-black/10 bg-[#F8F9FA] p-6">
            <Overline className="text-[9px]">{t("home.extract")}</Overline>
            <div className="mt-5 space-y-4">
              {c.extract.map(([l, s]) => (
                <div key={l}>
                  <div className="flex justify-between text-xs"><span>{l}</span><span className="font-mono-dt text-zinc-500">{s}/100</span></div>
                  <div className="mt-1.5 h-[2px] bg-zinc-200"><div className="h-full bg-black" style={{ width: `${s}%` }} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <Section className="border-b border-black/10 py-20 md:py-28">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <Overline>{t("home.measure.over")}</Overline>
            <h2 className="mt-6 max-w-2xl font-display text-3xl font-light leading-tight tracking-tight sm:text-4xl">{t("home.measure.title")}</h2>
          </div>
          <Link to="/science" data-testid="home-science-link" className="dt-link-underline inline-flex items-center gap-1.5 text-sm font-semibold">
            {t("cta.readMethod")} <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="mt-14 grid gap-px border border-black/10 bg-black/10 sm:grid-cols-2 lg:grid-cols-3">
          {c.indices.map(([label, note], i) => {
            const Icon = ICONS[i];
            return (
              <div key={label} data-testid={`index-card-${i}`} className="bg-white p-8 transition-colors hover:bg-[#F8F9FA]">
                <div className="flex items-start justify-between">
                  <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
                  <span className="font-mono-dt text-[10px] text-zinc-300">0{i + 1}</span>
                </div>
                <h3 className="mt-8 font-display text-2xl font-light">{label}</h3>
                <p className="mt-2 text-xs text-zinc-500">{note}</p>
              </div>
            );
          })}
        </div>
      </Section>

      <div className="border-b border-black/10 bg-[#0A0A0A] py-20 text-white md:py-28">
        <Section className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <Overline className="!text-zinc-500">{t("home.journey.over")}</Overline>
            <h2 className="mt-6 font-display text-3xl font-light leading-tight tracking-tight sm:text-4xl">{t("home.journey.title")}</h2>
            <CTAButton to="/how-it-works" testId="home-how-btn" variant="ghost" className="mt-10 !border-white/25 !text-white hover:!bg-white hover:!text-black">
              {t("nav.how")}
            </CTAButton>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            {c.journey.map(([n, title, body]) => (
              <div key={n} className="grid grid-cols-[auto_1fr] gap-6 border-t border-white/15 py-6">
                <span className="font-mono-dt text-xs text-zinc-500">{n}</span>
                <div>
                  <h3 className="font-display text-2xl font-light">{title}</h3>
                  <p className="mt-2 max-w-lg text-sm leading-relaxed text-zinc-400">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <Section className="grid items-center gap-12 border-b border-black/10 py-20 md:grid-cols-12 md:py-28">
        <div className="md:col-span-7">
          <div className="overflow-hidden border border-black/10 bg-[#F8F9FA]">
            <img src={IMAGES.kitHero} alt="Coffret DOCTHAIR ouvert : tube stérile, carte QR, notice et enveloppe retour" loading="lazy" width="1264" height="848" className="h-auto w-full object-cover" />
          </div>
        </div>
        <div className="md:col-span-5">
          <Overline>{t("home.kit.over")}</Overline>
          <h2 className="mt-6 font-display text-3xl font-light leading-tight tracking-tight sm:text-4xl">{t("home.kit.title")}</h2>
          <p className="mt-6 text-base leading-relaxed text-zinc-600">{t("home.kit.body")}</p>
          <div className="mt-8 space-y-3">
            {c.kit.map(([label], i) => (
              <div key={label} className="flex items-center gap-4 border-b border-black/10 pb-3">
                <span className="font-mono-dt text-[10px] text-zinc-400">0{i + 1}</span>
                <span className="text-sm">{label}</span>
              </div>
            ))}
          </div>
          <CTAButton to="/kit" testId="home-kit-btn" className="mt-10">{t("cta.discoverKit")}</CTAButton>
        </div>
      </Section>

      <Section className="py-20 md:py-32">
        <div className="relative overflow-hidden border border-black/10 bg-[#F8F9FA] p-10 md:p-20 dt-noise">
          <div className="relative max-w-2xl">
            <Overline>{t("home.final.over")}</Overline>
            <h2 className="mt-6 font-display text-4xl font-light leading-[0.95] tracking-tighter sm:text-5xl">{t("home.final.title")}</h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-zinc-600">{t("score.body")}</p>
            <CTAButton to="/order" testId="final-order-btn" className="mt-10">{t("cta.analyse")}</CTAButton>
          </div>
        </div>
      </Section>
    </>
  );
}

export function HowItWorks() {
  const { t, locale } = useI18n();
  const c = useContent(locale);
  return (
    <>
      <PageHero overline={t("home.journey.over")} title={t("how.title")} lead={t("home.journey.title")} image={IMAGES.microscope}>
        <CTAButton to="/order" testId="how-order-btn">{t("cta.analyse")}</CTAButton>
      </PageHero>
      <Section className="py-20 md:py-28">
        {c.journey.map(([n, title, body], i) => (
          <div key={n} data-testid={`how-step-${i}`} className="grid gap-6 border-t border-black/10 py-10 md:grid-cols-12">
            <div className="md:col-span-2"><span className="font-mono-dt text-xs text-zinc-400">{n}</span></div>
            <div className="md:col-span-4"><h2 className="font-display text-3xl font-light leading-tight">{title}</h2></div>
            <div className="md:col-span-5"><p className="text-base leading-relaxed text-zinc-600">{body}</p></div>
          </div>
        ))}
        <div className="mt-14 border border-black/10 bg-[#F8F9FA] p-8 md:p-12">
          <Overline>{t("how.automation.over")}</Overline>
          <h3 className="mt-4 max-w-2xl font-display text-2xl font-light">{t("how.automation.title")}</h3>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-zinc-600">{t("how.automation.body")}</p>
        </div>
      </Section>
    </>
  );
}

export function Science() {
  const { t, locale } = useI18n();
  const c = useContent(locale);
  return (
    <>
      <PageHero overline={t("nav.science")} title={t("science.title")} image={IMAGES.macroHair} lead={t("science.lead")} />
      <Section className="border-b border-black/10 py-20 md:py-28">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <Overline>{t("science.anatomy.over")}</Overline>
            <h2 className="mt-6 font-display text-3xl font-light leading-tight tracking-tight sm:text-4xl">{t("science.anatomy.title")}</h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            {c.anatomy.map(([title, body, tag]) => (
              <div key={title} className="grid gap-2 border-t border-black/10 py-7 sm:grid-cols-[180px_1fr]">
                <h3 className="font-display text-2xl font-light">{title}</h3>
                <div>
                  <p className="text-sm leading-relaxed text-zinc-600">{body}</p>
                  <p className="mt-2 font-mono-dt text-[10px] text-zinc-400">{tag}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>
      <Section className="border-b border-black/10 py-20 md:py-28">
        <Overline>{t("science.methods.over")}</Overline>
        <h2 className="mt-6 max-w-2xl font-display text-3xl font-light leading-tight tracking-tight sm:text-4xl">{t("science.methods.title")}</h2>
        <div className="mt-14">
          {c.methods.map(([n, title, body, params]) => (
            <div key={n} data-testid={`science-${n}`} className="grid gap-6 border-t border-black/10 py-10 md:grid-cols-12">
              <div className="md:col-span-2"><span className="font-mono-dt text-xs text-zinc-400">{n}</span></div>
              <div className="md:col-span-4">
                <h3 className="font-display text-3xl font-light leading-tight">{title}</h3>
                <div className="mt-4 flex flex-wrap gap-2">
                  {params.map((p) => <span key={p} className="border border-black/15 px-2.5 py-1 font-mono-dt text-[10px] text-zinc-500">{p}</span>)}
                </div>
              </div>
              <div className="md:col-span-5"><p className="text-base leading-relaxed text-zinc-600">{body}</p></div>
            </div>
          ))}
        </div>
      </Section>
      <div className="bg-[#0A0A0A] py-20 text-white md:py-28">
        <Section className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-6">
            <Overline className="!text-zinc-500">{t("science.limits.over")}</Overline>
            <h2 className="mt-6 font-display text-3xl font-light leading-tight sm:text-4xl">{t("science.limits.title")}</h2>
            <div className="mt-10 space-y-6">
              {c.limits.map(([a, b]) => (
                <div key={a} className="border-t border-white/15 pt-5">
                  <h3 className="font-display text-2xl font-light">{a}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400">{b}</p>
                </div>
              ))}
            </div>
            <CTAButton to="/order" testId="science-order-btn" variant="ghost" className="mt-12 !border-white/25 !text-white hover:!bg-white hover:!text-black">
              {t("cta.analyse")}
            </CTAButton>
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <div className="aspect-[3/4] overflow-hidden border border-white/15">
              <img src={IMAGES.hydration} alt="Mèche de cheveux hydratée en gros plan" loading="lazy" className="h-full w-full object-cover object-center" />
            </div>
          </div>
        </Section>
      </div>
    </>
  );
}

export function AnalysisPage() {
  const { t, locale } = useI18n();
  const [types, setTypes] = useState(() => catalogForLocale("fr"));
  const [metrics, setMetrics] = useState({});
  useEffect(() => {
    setTypes(catalogForLocale(locale));
    api.get("/catalog", { params: { locale } }).then(({ data }) => {
      if (validCatalog(data)) setTypes(data.analysis_types);
      if (data && typeof data.metrics === "object") setMetrics(data.metrics);
    }).catch(() => {});
  }, [locale]);
  return (
    <>
      <PageHero overline={t("nav.analysis")} title={t("analysis.title")} lead={t("score.body")}>
        <CTAButton to="/order" testId="analysis-order-btn">{t("cta.order")}</CTAButton>
      </PageHero>
      <Section className="py-16 md:py-24">
        <div className="grid gap-px border border-black/10 bg-black/10 md:grid-cols-3">
          {types.map((a) => (
            <div key={a.code} data-testid={`analysis-${a.code}`} className="bg-white p-8">
              <div className="flex items-baseline justify-between">
                <h2 className="font-display text-2xl font-light">{a.name}</h2>
                <span className="font-mono-dt text-lg">€{a.price}</span>
              </div>
              <p className="mt-3 text-sm text-zinc-600">{a.tagline}</p>
              <p className="mt-5 overline text-[9px]">{a.metrics.length} {t("analysis.params")} · {a.turnaround_days} {t("analysis.days")}</p>
              <ul className="mt-4 space-y-2">{a.features.map((f) => <li key={f} className="text-sm text-zinc-600">— {f}</li>)}</ul>
              <div className="mt-6 flex flex-wrap gap-1.5 border-t border-black/10 pt-4">
                {a.metrics.map((m) => <span key={m} className="font-mono-dt text-[9px] text-zinc-400">{metrics[m]?.label || m} ·</span>)}
              </div>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

export function KitPage() {
  const { t, locale } = useI18n();
  const c = useContent(locale);
  return (
    <>
      <div className="relative overflow-hidden border-b border-black/10 dt-noise">
        <div className="absolute inset-0 dt-grid-lines opacity-60" aria-hidden="true" />
        <Section className="relative grid gap-12 py-16 md:grid-cols-12 md:py-24">
          <div className="md:col-span-5 dt-reveal">
            <Overline>{t("home.kit.over")}</Overline>
            <h1 className="mt-6 font-display text-5xl font-light leading-[0.95] tracking-tighter sm:text-6xl">{t("home.kit.title")}</h1>
            <p className="mt-8 max-w-md text-base leading-relaxed text-zinc-600">{t("home.kit.body")}</p>
            <CTAButton to="/order" testId="kit-order-btn" className="mt-10">{t("cta.order")}</CTAButton>
          </div>
          <div className="md:col-span-7">
            <div className="overflow-hidden border border-black/10 bg-[#F8F9FA]">
              <img src={IMAGES.kitFlatlay} alt="Contenu du kit DOCTHAIR à plat : coffret, carte QR, notice, tube et enveloppe préaffranchie" loading="lazy" width="1264" height="848" className="h-auto w-full object-cover" />
            </div>
          </div>
        </Section>
      </div>
      <Section className="py-20 md:py-28">
        <Overline>{t("home.kit.inside.over")}</Overline>
        <h2 className="mt-6 font-display text-3xl font-light sm:text-4xl">{t("home.kit.inside.title")}</h2>
        <div className="mt-14 grid gap-px border border-black/10 bg-black/10 sm:grid-cols-2 lg:grid-cols-3">
          {c.kit.map(([label, body], i) => (
            <div key={label} data-testid={`kit-item-${i}`} className="bg-white p-8 transition-colors hover:bg-[#F8F9FA]">
              <span className="font-mono-dt text-[10px] text-zinc-300">0{i + 1}</span>
              <h3 className="mt-6 font-display text-2xl font-light">{label}</h3>
              <p className="mt-3 text-sm leading-relaxed text-zinc-500">{body}</p>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

export function HairScore() {
  const { t, locale } = useI18n();
  const c = useContent(locale);
  return (
    <>
      <PageHero overline="Index" title={t("score.title")} lead={t("score.body")} image={IMAGES.portrait} />
      <Section className="py-16 md:py-24">
        <div className="grid gap-10 md:grid-cols-3">
          {c.score.map(([a, b], i) => (
            <div key={a} className="border-t border-black/10 pt-6" data-testid={`score-block-${i}`}>
              <span className="font-mono-dt text-[10px] text-zinc-400">0{i + 1}</span>
              <h3 className="mt-4 font-display text-2xl font-light">{a}</h3>
              <p className="mt-3 text-sm leading-relaxed text-zinc-600">{b}</p>
            </div>
          ))}
        </div>
        <div className="mt-16 border border-black/10 p-8 md:p-12">
          <Overline>{t("score.example")}</Overline>
          <div className="mt-6 flex flex-wrap items-end gap-10">
            <p className="font-display text-6xl font-light">78<span className="text-2xl text-zinc-300">/100</span></p>
            <div className="grid flex-1 grid-cols-2 gap-4 sm:grid-cols-5">
              {c.scoreExample.map(([l, v]) => (
                <div key={l}><p className="font-mono-dt text-lg">{v}</p><p className="text-[11px] text-zinc-500">{l}</p></div>
              ))}
            </div>
          </div>
          <p className="mt-8 text-xs text-zinc-400">{t("legal.disclaimer")}</p>
        </div>
      </Section>
    </>
  );
}

export function Pricing() {
  const { t, locale } = useI18n();
  const [types, setTypes] = useState(() => catalogForLocale("fr"));
  useEffect(() => {
    setTypes(catalogForLocale(locale));
    api.get("/catalog", { params: { locale } })
      .then(({ data }) => { if (validCatalog(data)) setTypes(data.analysis_types); })
      .catch(() => {});
  }, [locale]);
  return (
    <>
      <PageHero overline={t("nav.pricing")} title={t("pricing.title")} lead={t("pricing.lead")} />
      <Section className="py-16 md:py-24">
        <div className="grid gap-px border border-black/10 bg-black/10 md:grid-cols-3">
          {types.map((a) => (
            <div key={a.code} data-testid={`price-${a.code}`} className="flex flex-col bg-white p-8">
              <h2 className="font-display text-2xl font-light">{a.name}</h2>
              <p className="mt-4 font-mono-dt text-3xl">€{a.price}</p>
              <p className="mt-3 text-sm text-zinc-600">{a.tagline}</p>
              <ul className="mt-6 flex-1 space-y-2">{a.features.map((f) => <li key={f} className="text-sm text-zinc-600">— {f}</li>)}</ul>
              <CTAButton to="/order" testId={`price-cta-${a.code}`} className="mt-8">{t("cta.order")}</CTAButton>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

export function ForProfessionals() {
  const { t, locale } = useI18n();
  const c = useContent(locale);
  return (
    <>
      <PageHero overline="B2B" title={t("pros.title")} image={IMAGES.specialist} lead={t("pros.lead")} />
      <Section className="py-16 md:py-24">
        <div className="grid gap-10 md:grid-cols-3">
          {c.pros.map(([a, b]) => (
            <div key={a} className="border-t border-black/10 pt-6">
              <h3 className="font-display text-2xl font-light">{a}</h3>
              <p className="mt-3 text-sm leading-relaxed text-zinc-600">{b}</p>
            </div>
          ))}
        </div>
        <CTAButton to="/contact" testId="pros-contact-btn" className="mt-12">{t("cta.talk")}</CTAButton>
      </Section>
    </>
  );
}

export function ForLaboratories() {
  const { t, locale } = useI18n();
  const c = useContent(locale);
  return (
    <>
      <PageHero overline={t("nav.partners")} title={t("labs.title")} image={IMAGES.labWork} lead={t("labs.lead")} />
      <Section className="py-16 md:py-24">
        <div className="grid gap-10 md:grid-cols-2">
          <div>
            <Overline>{t("labs.portal.over")}</Overline>
            <ul className="mt-5 space-y-3 text-sm text-zinc-600">
              {c.labPortal.map((l) => <li key={l}>— {l}</li>)}
            </ul>
          </div>
          <div>
            <Overline>{t("labs.api.over")}</Overline>
            <pre className="mt-5 overflow-x-auto border border-black/10 bg-[#0A0A0A] p-5 font-mono-dt text-[11px] leading-relaxed text-zinc-300">{`GET  /api/lab-api/sample/{sample_id}
POST /api/lab-api/sample-received
POST /api/lab-api/results
POST /api/lab-api/report

Header: X-Lab-Key: <votre clé>

Webhooks: sample_received, analysis_in_progress,
results_validation, report_available`}</pre>
          </div>
        </div>
        <CTAButton to="/contact" testId="labs-contact-btn" className="mt-12">{t("cta.talk")}</CTAButton>
      </Section>
    </>
  );
}

export function LabPartners() {
  const { t } = useI18n();
  const [labs, setLabs] = useState([]);
  // Même précaution qu'ailleurs : une réponse inattendue ne doit pas vider la page.
  useEffect(() => { api.get("/labs/public").then(({ data }) => { if (Array.isArray(data?.labs)) setLabs(data.labs); }).catch(() => {}); }, []);
  return (
    <>
      <PageHero overline={t("nav.partners")} title={t("partners.title")} lead={t("partners.lead")} />
      <Section className="py-16 md:py-24">
        <div className="grid gap-px border border-black/10 bg-black/10 md:grid-cols-3">
          {labs.map((l) => (
            <div key={l.name} className="bg-white p-8" data-testid={`partner-${l.country}`}>
              <p className="overline text-[9px]">{l.country}</p>
              <h3 className="mt-3 font-display text-2xl font-light">{l.name}</h3>
              <p className="mt-4 font-mono-dt text-xs text-zinc-500">{l.turnaround_days} {t("partners.turnaround")}</p>
              <p className="mt-2 text-sm text-zinc-600">{l.analysis_types.join(" · ")}</p>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

export function About() {
  const { t, locale } = useI18n();
  const c = useContent(locale);
  return (
    <>
      <PageHero overline="DOCTHAIR" title={t("about.title")} image={IMAGES.portraitCoily} lead={t("about.lead")} />
      <Section className="py-16 md:py-24">
        <div className="grid gap-10 md:grid-cols-3">
          {c.about.map(([a, b]) => (
            <div key={a} className="border-t border-black/10 pt-6">
              <h3 className="font-display text-2xl font-light">{a}</h3>
              <p className="mt-3 text-sm leading-relaxed text-zinc-600">{b}</p>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

export function Faq() {
  const { t, locale } = useI18n();
  const c = useContent(locale);
  return (
    <Section className="py-16 md:py-24">
      <div className="grid gap-14 md:grid-cols-12">
        <div className="md:col-span-4">
          <div className="sticky top-28">
            <Overline>{t("faq.over")}</Overline>
            <h1 className="mt-6 font-display text-4xl font-light leading-[0.95] tracking-tighter sm:text-5xl">{t("faq.title")}</h1>
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-zinc-600">{t("faq.lead")}</p>
            <CTAButton to="/contact" testId="faq-contact-btn" variant="ghost" className="mt-8">{t("cta.talk")}</CTAButton>
          </div>
        </div>
        <div className="md:col-span-7 md:col-start-6">
          <Accordion type="single" collapsible data-testid="faq-accordion">
            {c.faqs.map(([q, a], i) => (
              <AccordionItem key={q} value={`i-${i}`} className="border-b border-black/10">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-6 text-left font-display text-xl font-light hover:no-underline sm:text-2xl">{q}</AccordionTrigger>
                <AccordionContent className="pb-7 text-base leading-relaxed text-zinc-600">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </Section>
  );
}

export function Legal() {
  const { t, locale } = useI18n();
  const c = useContent(locale);
  const { pathname } = useLocation();
  const slug = pathname.split("/").pop();
  const [title, blocks] = c.legal[slug] || [t("legal.over"), []];
  return (
    <Section className="py-16 md:py-24">
      <Overline>{t("legal.over")}</Overline>
      <h1 className="mt-6 font-display text-4xl font-light tracking-tighter sm:text-5xl">{title}</h1>
      <div className="mt-12 max-w-3xl">
        {blocks.map(([a, b]) => (
          <div key={a} className="border-t border-black/10 py-7">
            <h2 className="font-display text-2xl font-light">{a}</h2>
            <p className="mt-3 text-base leading-relaxed text-zinc-600">{b}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}
